// src/unAuthPages/PricingPage.js — /pricing: Free and Pro, the full comparison, FAQ
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faMinus } from '@fortawesome/free-solid-svg-icons';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { AuthContext } from '../AuthContext';
import { MINUTE_OPTIONS, monthlyPrice } from '../myPlans/planPricing';
import { FREE_MESSAGE_LIMIT, PRO_MESSAGE_LIMIT } from '../myWorkspace/workspaceComponents/chat/chatModel';
import CtaBand from './site/CtaBand';
import Faq from './site/Faq';
import Seo, { SITE_URL } from './site/Seo';
import './pricing/pricing.css';

const FREE_FEATURES = [
  <><strong>60</strong> processing minutes</>,
  'YouTube processing',
  'Your first upload free to preview',
  'AI video timestamps',
  'Chat with your video',
  `${FREE_MESSAGE_LIMIT} chat messages per video`,
  'Answers in 11 languages',
];

const proFeatures = minutes => [
  'Everything in Free',
  <><strong>{minutes.toLocaleString()}</strong> processing minutes</>,
  <><strong>{PRO_MESSAGE_LIMIT}</strong> chat messages per video</>,
  'Local video processing',
  'Up to 3h 30m per video',
  '16 GB video size',
  'Publish and schedule to YouTube',
  'Description templates',
  'API access for YouTube links',
];

const YES = <FontAwesomeIcon icon={faCheck} className="price-yes" aria-label="Included" />;
const NO = <FontAwesomeIcon icon={faMinus} className="price-no" aria-label="Not included" />;
const YT = <FontAwesomeIcon icon={faYoutube} className="price-yt" aria-label="YouTube" />;

// [feature, Free, Pro]
const COMPARISON = [
  ['Fundamentals', [
    ['Industry-leading processing speed', YES, YES],
    ['Video max length', '60m', '3h 30m'],
    ['Video quality', 'SD, HD, 4K', 'SD, HD, 4K'],
    ['Import sources', YT, <>{YT} and local videos</>],
    ['API access', NO, YES],
    ['Upload local files', '1 free preview, up to 30m', YES],
    ['Max local file size', '16 GB', '16 GB'],
  ]],
  ['Bump AI', [
    ['Video timestamps', YES, YES],
    ['Chat with your video', YES, YES],
    ['Chat messages per video', String(FREE_MESSAGE_LIMIT), String(PRO_MESSAGE_LIMIT)],
    ['Answers in 11 languages', YES, YES],
  ]],
  ['Creator Studio', [
    ['Publish to YouTube', NO, YT],
    ['Schedule to YouTube', NO, YT],
    ['Description templates', NO, YES],
  ]],
];

const FeatureList = ({ items }) => (
  <ul className="price-features">
    {items.map((item, i) => (
      <li key={i}><FontAwesomeIcon icon={faCheck} className="price-yes" /> <span>{item}</span></li>
    ))}
  </ul>
);

const PricingPage = () => {
  const { currentUser } = useContext(AuthContext);
  const [period, setPeriod] = useState('monthly');
  const [minutes, setMinutes] = useState(MINUTE_OPTIONS[0]);
  const yearly = period === 'yearly';
  // Signed-in visitors upgrade from their plan settings instead of signing up again
  const proLink = currentUser ? '/settings/plan' : '/signup';

  return (
    <>
      <Seo
        title="Compare plans, features & costs | Bumpups.com"
        description="Choose between our free starter plan or upgrade to Pro for 1,000 or more processing minutes a month."
        jsonLd={{
          '@type': 'SoftwareApplication',
          name: 'Bumpups',
          url: SITE_URL,
          applicationCategory: 'MultimediaApplication',
          operatingSystem: 'Web',
          offers: [
            { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD', description: '60 processing minutes per month' },
            ...MINUTE_OPTIONS.map(m => ({
              '@type': 'Offer',
              name: `Pro · ${m.toLocaleString()} minutes`,
              price: String(monthlyPrice(m, 'monthly')),
              priceCurrency: 'USD',
              description: `${m.toLocaleString()} processing minutes per month, billed monthly`,
            })),
          ],
        }}
      />

      <section className="price-hero mk-hero-bg">
        <div className="mk-container mk-section-head">
          <h1 className="mk-h1">Plans &amp; pricing</h1>
          <p className="mk-lead">Simple, transparent pricing. No surprises.</p>
          <div className="price-toggle" role="radiogroup" aria-label="Billing period">
            <button type="button" role="radio" aria-checked={!yearly} className={!yearly ? 'active' : ''} onClick={() => setPeriod('monthly')}>
              Monthly
            </button>
            <button type="button" role="radio" aria-checked={yearly} className={yearly ? 'active' : ''} onClick={() => setPeriod('yearly')}>
              Annual <span className="price-save">Save 20%</span>
            </button>
          </div>
        </div>
      </section>

      <section className="mk-container price-plans">
        <div className="price-card">
          <h2 className="price-name">Free</h2>
          <p className="price-tagline">Perfect to get started</p>
          <p className="price-amount"><span className="price-number">$0</span><span className="price-unit">/month</span></p>
          <p className="price-billing">No credit card needed</p>
          <Link to={currentUser ? '/workspace' : '/signup'} className="mk-btn price-cta">
            {currentUser ? 'Open Workspace' : 'Create an account'}
          </Link>
          <FeatureList items={FREE_FEATURES} />
        </div>

        <div className="price-card price-card--pro">
          <h2 className="price-name">Pro</h2>
          <p className="price-tagline price-tagline--accent">Best for power users</p>
          <div className="price-minutes" role="radiogroup" aria-label="Monthly minutes">
            {MINUTE_OPTIONS.map(m => (
              <button key={m} type="button" role="radio" aria-checked={minutes === m} className={minutes === m ? 'active' : ''} onClick={() => setMinutes(m)}>
                {m.toLocaleString()} min
              </button>
            ))}
          </div>
          <p className="price-amount">
            {yearly && <span className="price-old">${monthlyPrice(minutes, 'monthly')}</span>}
            <span className="price-number">${monthlyPrice(minutes, period)}</span>
            <span className="price-unit">/month</span>
          </p>
          <p className="price-billing">
            {yearly ? `Billed annually ($${monthlyPrice(minutes, 'yearly') * 12}/year)` : 'Billed monthly · cancel anytime'}
          </p>
          <Link to={proLink} className="mk-btn mk-btn--primary price-cta">
            {currentUser ? 'Upgrade to Pro' : 'Get started for free'} <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
          </Link>
          <FeatureList items={proFeatures(minutes)} />
        </div>

        <div className="price-enterprise">
          <div>
            <h2 className="price-name">Need more?</h2>
            <p className="price-tagline">Bumpups Enterprise brings video intelligence to your whole organization.</p>
          </div>
          <Link to="/enterprise" className="mk-btn">
            Bumpups Enterprise <FontAwesomeIcon icon={faArrowRight} className="mk-btn-arrow" />
          </Link>
        </div>
      </section>

      <section className="mk-section">
        <div className="mk-container">
          <div className="mk-section-head">
            <h2 className="mk-h2">Choose the plan that’s right for you</h2>
          </div>
          <div className="price-table-wrap">
            <table className="price-table">
              <thead>
                <tr>
                  <th scope="col"><span className="price-sr">Feature</span></th>
                  <th scope="col">Free</th>
                  <th scope="col">Pro</th>
                </tr>
              </thead>
              {COMPARISON.map(([group, rows]) => (
                <tbody key={group}>
                  <tr className="price-group">
                    <th scope="colgroup" colSpan="3">{group}</th>
                  </tr>
                  {rows.map(([feature, free, pro]) => (
                    <tr key={feature}>
                      <th scope="row">{feature}</th>
                      <td>{free}</td>
                      <td>{pro}</td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        </div>
      </section>

      <Faq />
      <CtaBand />
    </>
  );
};

export default PricingPage;
